import { ref, reactive, watch, toRef } from "@vue/composition-api";
import axiosIns from "@/libs/axios";
import reFetchAPI from "@/utils/refetch-api";
import { errorNotification } from "@/utils/toast-notifications";

export default function useFineList() {
  const tableColumns = [
    { key: 'amount' },
    { key: 'number', label: 'Trip #' },
    { key: 'fine_state', label: 'Status' },
    { key: 'created_at', label: 'Created At' },
    { key: 'action_by', label: 'Action By' },
    { key: 'actions' },
  ]
  const fineStateOptions = [
    {
      label: "In Review",
      value: "in_review",
    },
    {
      label: "Processed",
      value: "processed",
    },
    {
      label: "Rejected",
      value: "rejected",
    },
  ];
  const queryParams = reactive({
    perPage: 10,
    page: 1,
    fineState: "in_review",
    number: "",
    created_at_from: "",
    created_at_to: "",
  });
  const navigateToTripDetails = (id) =>
    window.open(`${window.location.origin}/trips/${id}`);
  const pageLoading = ref(false);
  const fines = ref([]);
  const totalFines = ref(0);
  const fetchFineTransactions = async () => {
    if (pageLoading.value) return;
    try {
      pageLoading.value = true;
      const params = {
        page: queryParams.page,
        size: queryParams.perPage,
      };
      if (queryParams.fineState) {
        params.fine_state = queryParams.fineState;
      }
      if (queryParams.number) {
        params.number = queryParams.number;
      }
      if (queryParams.created_at_from) {
        params.created_at_from = queryParams.created_at_from;
      }
      if (queryParams.created_at_to) {
        params.created_at_to = queryParams.created_at_to;
      }
      const response = await axiosIns.get("transactions/fine-events", {
        params,
      });
      fines.value = response.data.data.event_fine_transactions;
      totalFines.value = response.data.data.total;
    } catch (error) {
      if (error.message.includes("timeout")) {
        fetchFineTransactions();
      } else {
        reFetchAPI(fetchFineTransactions);
        errorNotification("Something went wrong!!, retrying..");
      }
    } finally {
      pageLoading.value = false;
    }
  };
  const updateFineTransaction = async (id, action) => {
    try {
      pageLoading.value = true;
      await axiosIns.patch(`transactions/fine-events/${id}`, {
        action,
      });
    } catch (error) {
      errorNotification("Something went wrong!!, retrying..");
    } finally {
      pageLoading.value = false;
      await fetchFineTransactions();
    }
  };
  const isExporting = ref(false);
  let pollingInterval = null;

  const stopPolling = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      pollingInterval = null;
    }
  };

  const pollExportStatus = async () => {
    try {
      const response = await axiosIns.get("/transactions/fine-events/exports");
      const exportFiles = response.data?.data?.data;
      
      if (exportFiles && exportFiles.length > 0) {
        const firstFile = exportFiles[0];
        const lastModified = new Date(firstFile.lastModified);
        const now = new Date();
        const diffInSeconds = Math.abs((now - lastModified) / 1000);
        
        if (diffInSeconds <= 30) {
          stopPolling();
          
          const downloadResponse = await axiosIns.post(
            "/transactions/fine-events/export-download-url",
            { key: firstFile.key }
          );
          
          const downloadURL = downloadResponse.data.data.presignedUrl;
          window.open(downloadURL, "_blank");
          
          isExporting.value = false;
        }
      }
    } catch (error) {
      errorNotification("Error processing export");
      isExporting.value = false;
      stopPolling();
    }
  };

  const exportToCSV = async () => {
    try {
      isExporting.value = true;
      
      const requestBody = {};
      if (queryParams.fineState) {
        requestBody.fine_state = queryParams.fineState;
      }
      if (queryParams.number) {
        requestBody.number = queryParams.number;
      }
      if (queryParams.created_at_from) {
        requestBody.created_at_from = queryParams.created_at_from;
      }
      if (queryParams.created_at_to) {
        requestBody.created_at_to = queryParams.created_at_to;
      }

      await axiosIns.post("transactions/fine-events/export-job", requestBody);
      
      stopPolling();
      pollingInterval = setInterval(pollExportStatus, 10000);
      pollExportStatus();
      
    } catch (error) {
      errorNotification("Failed to start export job");
      isExporting.value = false;
      stopPolling();
    }
  };
  watch([toRef(queryParams, "page"), toRef(queryParams, "perPage")], () => {
    fetchFineTransactions();
  });
  watch(
    () => queryParams.activeState,
    () => {
      fetchFineTransactions();
      queryParams.page = 1;
    }
  );

  fetchFineTransactions();
  return {
    tableColumns,
    fineStateOptions,
    pageLoading,
    fines,
    totalFines,
    queryParams,
    fetchFineTransactions,
    navigateToTripDetails,
    updateFineTransaction,
    exportToCSV,
    isExporting,
    stopPolling,
  };
}
